import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  EmployeeField,
  FieldInput,
  useDefaultValues,
  useFormDataToUpdateFields,
  useValidationSchema,
} from '../../../components/EmployeeData';
import ModalSidebar, {
  ModalSidebarFormContent,
} from '../../../components/ModalSidebar';
import { ExtractProps } from '../../../../../utils/react';
import {
  EmployeeDataFieldState,
  useUpdateWorkflowItemDataRequestMutation,
  WorkflowItemStatus as WorkflowItemStatusGenerated,
} from '../../../__generated__/graphql';

import { WorkflowItemCommonFields } from './WorkflowItemCommonFields';
import useYupValidationResolver from '../../../../../components/form/useYupValidationResolver';
import {
  WorkflowItemDataRequest,
  WorkflowItemStatus,
} from '../WorkflowItems/types';

import { WorkflowItemStatusAutoSave } from './WorkflowItemStatusAutoSave';
import { WorkflowItemSubmitButton } from './WorkflowItemSubmitButton';

type ModalSidebarProps = Pick<ExtractProps<typeof ModalSidebar>, 'onClose'>;

type Props = ModalSidebarProps & {
  workflowItem: WorkflowItemDataRequest;
  dataFields: EmployeeField[];
  onClose: () => void;
};

type FormData = {
  status: WorkflowItemStatus;
  [key: string]: any;
};

export const WorkflowItemDataRequestForm: React.FC<
  React.PropsWithChildren<Props>
> = ({ workflowItem, dataFields, onClose }) => {
  const { t } = useTranslation();
  const { status } = workflowItem;
  const dataFieldsWithState = useMemo(
    () =>
      dataFields.map((x) => ({
        ...x,
        state: x.fieldInfo.required
          ? EmployeeDataFieldState.Required
          : EmployeeDataFieldState.Edit,
      })),
    [dataFields],
  );

  const hasRequiredFields = useMemo(
    () => dataFields.some((x) => x.fieldInfo.required),
    [dataFields],
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [updateItem] = useUpdateWorkflowItemDataRequestMutation();

  const errorHandler = useMutationErrorHandler();

  const formDataToInputFields = useFormDataToUpdateFields();
  const dataFieldsDefaultValues = useDefaultValues(dataFields);
  const validationSchema = useValidationSchema(dataFieldsWithState);

  const defaultValues: FormData = {
    status,
    ...dataFieldsDefaultValues,
  };

  const form = useForm<FormData>({
    defaultValues,
    validationResolver: useYupValidationResolver(validationSchema),
  });

  const dirtyFields = form.formState.dirtyFields;

  // Compute this from the persisted `dataFields` (server values), not form state.
  // We only want the status dropdown to become enabled once the required fields
  // have actually been saved — otherwise a user could type values, change the
  // status (which persists immediately via auto-save), and refresh to find
  // that the status changed but the field values were never saved.
  const hasUnfilledRequiredFields = dataFields.some((field) => {
    if (!field.fieldInfo.required) {
      return false;
    }
    return (
      field.value === null || field.value === undefined || field.value === ''
    );
  });

  const handleSubmit = (formData: FormData, dirtyFields: Set<string>) => {
    setIsLoading(true);
    const updateFields = formDataToInputFields(
      dataFields,
      formData,
      dirtyFields,
    );

    let status = formData.status ? formData.status : workflowItem.status;

    //Automatically update status if any field is updated
    if (
      !workflowItem.readonly &&
      //Updating status is not from the status dropdown
      !formData.status &&
      status === WorkflowItemStatusGenerated.NotStarted
    ) {
      status = WorkflowItemStatusGenerated.InProgress;
    }

    return updateItem({
      variables: {
        input: {
          id: workflowItem.id,
          status: status as WorkflowItemStatusGenerated,
          dataFields: updateFields,
        },
      },
    })
      .then(() => {
        //If not updating the status from the dropdown, then close the sidebar
        //This was used previously as a workaround to overcome an issue with apollo cache
        if (!formData.status) {
          onClose();
        }
      })
      .catch(errorHandler)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onStatusChange = (status: WorkflowItemStatus) => {
    if (hasUnfilledRequiredFields) {
      return;
    }
    handleSubmit({ status }, dirtyFields);
  };

  const handleStatusLoading = (isStatusLoading: boolean) => {
    setIsLoading(isStatusLoading);
  };

  return (
    <ModalSidebarFormContent
      form={form}
      onSubmit={(formData) => handleSubmit(formData, dirtyFields)}
      body={
        <>
          <WorkflowItemCommonFields
            workflowItem={workflowItem}
            onStatusLoading={handleStatusLoading}
          />
          {hasRequiredFields && !workflowItem.readonly && (
            <p className="text-muted small mb-3">
              {t('performanceReview.workflowsItems.sidebar.requiredFieldsNote')}
            </p>
          )}
          {dataFields.map((dataField, index) => (
            <FieldInput
              key={index}
              fieldInfo={{
                ...dataField.fieldInfo,
                disabled: workflowItem.readonly || isLoading,
              }}
            />
          ))}
          {!workflowItem.readonly ? (
            <WorkflowItemSubmitButton disabled={isLoading} />
          ) : undefined}
          <WorkflowItemStatusAutoSave
            value={status}
            onChange={onStatusChange}
            readonly={workflowItem.readonly}
            saving={isLoading}
            saved={!isLoading}
            disabled={hasUnfilledRequiredFields || isLoading}
          />
        </>
      }
    />
  );
};
